import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TableComponent from 'components/Table';
import { TaskPlannedDate, TaskTimeWindowProgress } from './TaskFieldRenderer';
import {
  mapStatus,
  sortByDaysRemainingInTimeWindow,
  sortByDue,
  sortByTaskStatus,
} from 'screens/task-management/util';
import { renderInfoValue } from 'utils/helpers';
import type { Task as TaskAssignment } from 'screens/task-management/types';
import type { TaskStatus } from 'types/common';
import StatusTag from 'components/StatusTag';
import ClickableFirstCol from 'components/Table/ClickableFirstCol';
import { TableProps } from 'antd';

interface TaskTableProps {
  onRowClick?: (record: TaskAssignment) => void;
  data: TaskAssignment[] | undefined;
  loading: boolean;
  hiddenColumnKeys?: TaskTableColumnKey[];
  rowClassName?: TableProps<TaskAssignment>['rowClassName'];
}

type TaskTableColumnKey =
  | 'taskDescription'
  | 'status'
  | 'agronomistName'
  | 'plantingGroupName'
  | 'plannedDate'
  | 'timeWindow';

const TasksTable = ({
  data,
  loading,
  onRowClick,
  hiddenColumnKeys,
  ...props
}: TaskTableProps) => {
  const { t } = useTranslation();

  const renderTableHeader = () =>
    useMemo(
      () =>
        [
          {
            title: t('taskManagement.labels.task'),
            dataIndex: ['taskDescription'],
            key: 'taskDescription',
            width: 180,
            className: 'clickable',
            render: (text: string) => <ClickableFirstCol value={text} />,
            onCell: (record: TaskAssignment) => ({
              onClick: () => onRowClick?.(record),
            }),
          },
          {
            title: t('taskManagement.labels.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: TaskStatus) => {
              return <StatusTag status={mapStatus(status)} />;
            },
            sorter: sortByTaskStatus,
            width: 100,
          },
          {
            title: t('taskManagement.labels.agronomist'),
            dataIndex: 'agronomistName',
            key: 'agronomistName',
            width: 150,
            render: (text: string) => renderInfoValue(text),
          },
          {
            title: t('taskManagement.labels.plantingGroup'),
            dataIndex: 'plantingGroupName',
            key: 'plantingGroupName',
            width: 150,

            render: (text: string) => renderInfoValue(text),
          },
          {
            title: t('taskManagement.labels.plannedDate'),
            dataIndex: 'plannedDate',
            key: 'plannedDate',
            render: (date: string, record: TaskAssignment) => (
              <TaskPlannedDate date={date} status={record?.status} />
            ),
            sorter: sortByDue,
            width: 150,
          },
          {
            title: t('taskManagement.labels.timeWindow'),
            dataIndex: 'timeWindow',
            key: 'timeWindow',
            render: (_: string, record: TaskAssignment) => (
              <TaskTimeWindowProgress record={record} />
            ),
            sorter: sortByDaysRemainingInTimeWindow,
            width: 150,
          },
        ].filter(
          (val) =>
            !hiddenColumnKeys ||
            !hiddenColumnKeys.includes(val.key as TaskTableColumnKey),
        ),
      [t, hiddenColumnKeys],
    );

  // NOTE: As of writing this we have no pagination available for the data rendered in the table
  // Hence, a large number of rows are rendered most of the times,
  // I have enabled virtual scroll for the table as I observed the
  // lag while applying filters and sorting, enabling virtual scroll seems to have improved the performance
  // We can remove this when we have pagination in place
  // Varadarajan

  return (
    <TableComponent<TaskAssignment>
      virtual
      tableLayout="fixed"
      scroll={{ y: 300 }}
      loading={loading}
      pagination={false}
      rowKey={'taskId'}
      tableColumns={data}
      tableHeader={renderTableHeader()}
      // to minimize cumulative layout shift
      style={{
        paddingBottom: 16,
      }}
      {...props}
    />
  );
};

export default TasksTable;
